import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Lightbulb, ListChecks, Vote } from "lucide-react";

import { useTrip } from "@/hooks/use-trip";
import { Skeleton } from "@/components/ui/skeleton";
import { PlanActionBar, type PlanSection } from "@/components/plan-action-bar";
import { TRIP_ACTION_BAR_SPACER } from "@/components/trip-action-bar";
import { cn } from "@/lib/utils";
import { isTripReadOnly } from "@/lib/trip-readonly";
import { IdeasSection } from "@/features/plan/ideas-section";
import { PollsSection } from "@/features/plan/polls-section";
import { PackingSection } from "@/features/plan/packing-section";

/**
 * The "Plan" trip-detail tab. Container for three planning surfaces:
 *   - Ideas   → wishlist of things to do (no dates required)
 *   - Polls   → group decisions ("beach or museum?")
 *   - Packing → bring-list with claim system
 *
 * The parent trip layout hides its own action bar on the Plan tab; this
 * tab owns its own bottom action bar (PlanActionBar) whose CTA copy and
 * behavior track the active sub-section. Add-sheets are owned here too
 * — sub-sections receive `adding` + `onClose` props and render the sheet
 * only when told. Keeps the "add" affordance in one place users always
 * expect: the bottom of the screen.
 */
export default function PlanTab() {
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading } = useTrip(id);
  const [section, setSection] = useState<PlanSection>("ideas");
  const [adding, setAdding] = useState(false);
  const readOnly = isTripReadOnly(trip);

  const openAdd = useCallback(() => setAdding(true), []);
  const closeAdd = useCallback(() => setAdding(false), []);

  // Switching sub-sections implicitly closes any add-sheet the previous
  // section had open — otherwise the sheet stays mounted with stale context.
  const switchSection = useCallback((s: PlanSection) => {
    setAdding(false);
    setSection(s);
  }, []);

  if (isLoading || !trip) {
    return (
      <div className={cn("mx-auto max-w-2xl px-5 pt-4 flex flex-col gap-3", TRIP_ACTION_BAR_SPACER)} aria-busy>
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "mx-auto max-w-2xl px-5 pt-4 flex flex-col gap-4",
          TRIP_ACTION_BAR_SPACER,
        )}
      >
        <SubTabBar section={section} onChange={switchSection} />
        <div role="tabpanel" aria-labelledby={`plan-tab-${section}`}>
          {section === "ideas" ? (
            <IdeasSection trip={trip} adding={adding && !readOnly} onCloseAdd={closeAdd} readOnly={readOnly} />
          ) : null}
          {section === "polls" ? (
            <PollsSection trip={trip} adding={adding && !readOnly} onCloseAdd={closeAdd} readOnly={readOnly} />
          ) : null}
          {section === "packing" ? (
            <PackingSection trip={trip} adding={adding && !readOnly} onCloseAdd={closeAdd} readOnly={readOnly} />
          ) : null}
        </div>
      </div>

      {/* Read-only trips (past) hide the whole 'new' action bar */}
      {readOnly ? null : <PlanActionBar section={section} onNew={openAdd} />}
    </>
  );
}

// ─── Sub-tab bar (segmented control) ────────────────────────
function SubTabBar({
  section,
  onChange,
}: {
  section: PlanSection;
  onChange: (s: PlanSection) => void;
}) {
  const items: Array<{ key: PlanSection; label: string; icon: typeof Lightbulb }> = [
    { key: "ideas", label: "Ideas", icon: Lightbulb },
    { key: "polls", label: "Polls", icon: Vote },
    { key: "packing", label: "Packing", icon: ListChecks },
  ];
  return (
    <div role="tablist" aria-label="Plan sections" data-tour="plan-sections" className="grid grid-cols-3 gap-1 rounded-full bg-bg-sunken p-1">
      {items.map((it) => {
        const on = section === it.key;
        return (
          <button
            key={it.key}
            id={`plan-tab-${it.key}`}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(it.key)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-[13px] font-semibold transition-colors",
              on ? "bg-bg-elevated text-ink shadow-warm-1" : "text-ink-subtle hover:text-ink",
            )}
          >
            <it.icon className="h-3.5 w-3.5" />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
